import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { eventServices } from '../../services/eventServices';

// Async thunks
export const createClientWithEvent = createAsyncThunk(
  'events/createClientWithEvent',
  async (formData, { rejectWithValue }) => {
    try {
      return await eventServices.createClientWithEvent(formData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getAllClientImages = createAsyncThunk(
  'events/getAllClientImages',
  async ({ eventCode, page, limit }, { rejectWithValue }) => {
    try {
      return await eventServices.getAllClientImages(eventCode, page, limit);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Accepts either a raw FormData (back-compat) or
// `{ formData, onUploadProgress, sessionManaged }`. The batcher passes
// `sessionManaged: true` because it owns the loading/progress lifecycle for
// the whole multi-batch session -- individual batch thunks then skip their
// own loading/success flips so the UI bar doesn't flicker between batches.
const normalizeUploadArg = (arg) => {
  if (arg instanceof FormData) {
    return { formData: arg, onUploadProgress: undefined, sessionManaged: false };
  }
  return {
    formData: arg?.formData,
    onUploadProgress: arg?.onUploadProgress,
    sessionManaged: Boolean(arg?.sessionManaged),
  };
};

export const uploadImages = createAsyncThunk(
  'events/upload',
  async (arg, { rejectWithValue }) => {
    const { formData, onUploadProgress } = normalizeUploadArg(arg);
    try {
      return await eventServices.uploadImages(formData, { onUploadProgress });
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const uploadVideos = createAsyncThunk(
  'events/uploadVideos',
  async (arg, { rejectWithValue }) => {
    const { formData, onUploadProgress } = normalizeUploadArg(arg);
    try {
      return await eventServices.uploadVideos(formData, { onUploadProgress });
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// True when the thunk was dispatched as part of a session that the upload
// batcher owns (it set loading/success/progress before the first dispatch
// and will clear them after the last). Reducers below use this to avoid
// flicker between batches.
const isSessionManaged = (action) => Boolean(action?.meta?.arg?.sessionManaged);

export const addComment = createAsyncThunk(
  'events/addComment',
  async (commentData, { rejectWithValue }) => {
    try {
      return await eventServices.addComment(commentData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  currentEvent: null,
  images: [],
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  },
  loading: false,
  error: null,
  uploadStatus: {
    loading: false,
    error: null,
    success: false,
    progress: {
      loaded: 0,
      total: 0,
      filesCompleted: 0,
      filesTotal: 0,
    },
  },
};

const emptyProgress = () => ({
  loaded: 0,
  total: 0,
  filesCompleted: 0,
  filesTotal: 0,
});

const eventSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    resetUploadStatus: (state) => {
      state.uploadStatus = {
        loading: false,
        error: null,
        success: false,
        progress: emptyProgress(),
      };
    },
    clearError: (state) => {
      state.error = null;
    },
    // Driven by uploadBatching. Aggregates byte progress across concurrent
    // batches so the UI bar reflects ACTUAL upload progress instead of a
    // canned 70% animation.
    setUploadProgress: (state, action) => {
      const { loaded, total, filesCompleted, filesTotal } = action.payload || {};
      state.uploadStatus.progress = {
        loaded: typeof loaded === 'number' ? loaded : state.uploadStatus.progress.loaded,
        total: typeof total === 'number' ? total : state.uploadStatus.progress.total,
        filesCompleted: typeof filesCompleted === 'number' ? filesCompleted : state.uploadStatus.progress.filesCompleted,
        filesTotal: typeof filesTotal === 'number' ? filesTotal : state.uploadStatus.progress.filesTotal,
      };
    },
    beginUploadSession: (state, action) => {
      const { totalBytes = 0, totalFiles = 0 } = action.payload || {};
      state.uploadStatus.loading = true;
      state.uploadStatus.error = null;
      state.uploadStatus.success = false;
      state.uploadStatus.progress = {
        loaded: 0,
        total: totalBytes,
        filesCompleted: 0,
        filesTotal: totalFiles,
      };
    },
    endUploadSession: (state, action) => {
      const { success } = action.payload || {};
      state.uploadStatus.loading = false;
      state.uploadStatus.success = Boolean(success);
      // Keep progress visible briefly so the bar can finish; UploadProgress
      // clears it via resetUploadStatus once the success toast fades.
    },
  },
  extraReducers: (builder) => {
    builder
      // createClientWithEvent
      .addCase(createClientWithEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createClientWithEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.currentEvent = action.payload.event;
      })
      .addCase(createClientWithEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // getAllClientImages
      .addCase(getAllClientImages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllClientImages.fulfilled, (state, action) => {
        state.loading = false;
        state.images = action.payload.images;
        state.pagination = action.payload.pagination;
      })
      .addCase(getAllClientImages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // uploadImages
      .addCase(uploadImages.pending, (state, action) => {
        if (isSessionManaged(action)) return;
        state.uploadStatus.loading = true;
        state.uploadStatus.error = null;
        state.uploadStatus.success = false;
      })
      .addCase(uploadImages.fulfilled, (state, action) => {
        if (isSessionManaged(action)) return;
        state.uploadStatus.loading = false;
        state.uploadStatus.success = true;
      })
      .addCase(uploadImages.rejected, (state, action) => {
        if (isSessionManaged(action)) {
          // Batcher will surface the error; record it but don't toggle loading.
          state.uploadStatus.error = action.payload;
          return;
        }
        state.uploadStatus.loading = false;
        state.uploadStatus.error = action.payload;
      })

      // uploadVideos
      .addCase(uploadVideos.pending, (state, action) => {
        if (isSessionManaged(action)) return;
        state.uploadStatus.loading = true;
        state.uploadStatus.error = null;
        state.uploadStatus.success = false;
      })
      .addCase(uploadVideos.fulfilled, (state, action) => {
        if (isSessionManaged(action)) return;
        state.uploadStatus.loading = false;
        state.uploadStatus.success = true;
      })
      .addCase(uploadVideos.rejected, (state, action) => {
        if (isSessionManaged(action)) {
          state.uploadStatus.error = action.payload;
          return;
        }
        state.uploadStatus.loading = false;
        state.uploadStatus.error = action.payload;
      })

      // addComment
      .addCase(addComment.pending, (state) => {
        state.uploadStatus.loading = true;
        state.uploadStatus.error = null;
        state.uploadStatus.success = false;
      })
      .addCase(addComment.fulfilled, (state) => {
        state.uploadStatus.loading = false;
        state.uploadStatus.success = true;
      })
      .addCase(addComment.rejected, (state, action) => {
        state.uploadStatus.loading = false;
        state.uploadStatus.error = action.payload;
      });
  },
});

// Selectors
export const selectCurrentEvent = (state) => state.events.currentEvent;
export const selectImages = (state) => state.events.images;
export const selectPagination = (state) => state.events.pagination;
export const selectLoading = (state) => state.events.loading;
export const selectError = (state) => state.events.error;
export const selectUploadStatus = (state) => state.events.uploadStatus;

export const {
  resetUploadStatus,
  clearError,
  setUploadProgress,
  beginUploadSession,
  endUploadSession,
} = eventSlice.actions;

export const selectUploadProgress = (state) => state.events.uploadStatus.progress;

export default eventSlice.reducer; 